import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../common/Icon';

/**
 * Modal — the canonical centered dialog for Alive. Provides focus
 * lock-ish behavior (returns focus to the trigger on close), escape
 * key dismissal, overlay click dismissal, and body scroll lock.
 *
 * Design language baked in:
 *
 *   - Surface uses the elevated dark token (`#171719`), light = white
 *   - Flat 4px radius — never `rounded-2xl`
 *   - Hairline divider between header / body / footer
 *   - Header has a left-aligned title and a right-aligned close button
 *   - Body is scrollable when content overflows
 *   - Footer is sticky at the bottom of the modal frame
 *   - On mobile (`<sm`), the modal anchors to the bottom and slides
 *     up — same component, different layout, no extra Sheet wrapper
 *
 * For a yes/no confirmation use `<ConfirmDialog>`. For a generic
 * bottom-sheet picker use `<Sheet>`. For a list of action options
 * use the existing `<ActionSheet>` from `@/components/common`.
 *
 * @example
 * <Modal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="New conversation"
 *   description="Pick agents to start a chat with."
 *   footer={
 *     <>
 *       <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button onClick={onCreate}>Create</Button>
 *     </>
 *   }
 * >
 *   <List>...</List>
 * </Modal>
 */
export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  /** When `false`, escape and overlay clicks no longer dismiss. */
  dismissible?: boolean;
  /** Width preset. `sm` (~400px), `md` (~520px, default), `lg` (~720px). */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  hideCloseButton,
  dismissible = true,
  size = 'md',
}: ModalProps) {
  useOverlayBehavior(open, onOpenChange, dismissible);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 animate-in fade-in-0 duration-200"
        onClick={() => { if (dismissible) onOpenChange(false); }}
      />

      {/* Frame */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`
          relative w-full ${SIZE_CLASS[size]}
          max-h-[85vh] sm:max-h-[80vh]
          flex flex-col
          bg-white dark:bg-[#171719] text-gray-900 dark:text-gray-100
          rounded-t-lg sm:rounded shadow-2xl
          animate-in fade-in-0 slide-in-from-bottom-4 sm:zoom-in-95 sm:slide-in-from-bottom-0 duration-200
        `}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex-1 min-w-0">
              {title && (
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="
                  flex-shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full flex items-center justify-center
                  text-gray-500 dark:text-gray-400
                  hover:bg-gray-100 dark:hover:bg-white/[0.06]
                  transition-colors
                "
              >
                <Icon name="close" size={18} />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-white/[0.06]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Shared overlay behavior ──────────────────────────────────────────────

/**
 * Wires up escape-to-dismiss, body scroll lock, and focus restoration
 * for any portaled overlay (Modal, Sheet, ConfirmDialog).
 */
export function useOverlayBehavior(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  dismissible: boolean,
) {
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    // Capture the element that had focus when the overlay opened so
    // we can restore it when the overlay closes.
    triggerRef.current = document.activeElement;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleKey);

    // Body scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
      if (triggerRef.current && 'focus' in triggerRef.current) {
        (triggerRef.current as HTMLElement).focus();
      }
    };
  }, [open, dismissible, onOpenChange]);
}

// ─── ConfirmDialog ─────────────────────────────────────────────────────────

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  intent?: 'primary' | 'danger';
  onConfirm: () => void;
}

/**
 * Convenience wrapper around `<Modal>` for "are you sure?" dialogs.
 *
 * @example
 * <ConfirmDialog
 *   open={confirmOpen}
 *   onOpenChange={setConfirmOpen}
 *   title="Delete this conversation?"
 *   description="The messages and history can't be recovered."
 *   intent="danger"
 *   confirmLabel="Delete"
 *   onConfirm={handleDelete}
 * />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  intent = 'primary',
  onConfirm,
}: ConfirmDialogProps) {
  const confirmClass =
    intent === 'danger'
      ? 'bg-red-500 hover:bg-red-600 text-white'
      : 'bg-primary hover:bg-primary/90 text-white';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-white/[0.12] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {/* Empty body — the description in the header is enough. */}
      <div className="px-4 py-2" />
    </Modal>
  );
}
